import { History, Location } from 'history';
import { Store } from 'redux';
import { routes } from '../config';
import { RootState } from '../interfaces';
import { Asset } from '../queries/ats/titles/types/asset';
import { Title } from '../queries/ats/titles/types/title';
import { warn } from '../utils/commonUtils';
import { DetailsSearchParams } from '../views/ats-details/use-details-search-params';
import { DetailsParams } from '../views/ats-details/utils';
import {
  decrementStack,
  incrementStack,
  removeBackgroundLocation,
  resetStack,
  setBackgroundLocation,
} from './redux/actions';

interface DetailsRouterPathOptions extends DetailsParams {
  params?: Partial<DetailsSearchParams>;
}

export class DetailsRouter {
  public background: Location<unknown> | undefined;
  private stack: number;
  private prevLocation: Location<unknown>;
  private scrollPosition: number;
  private unregister: () => void;

  constructor(private history: History<unknown>, private store: Store<RootState>) {
    this.prevLocation = history.location;
    this.scrollPosition = document.documentElement.scrollTop;

    const router = this.store.getState().router;

    this.background = router.background;
    this.stack = router.stack;

    this.store.subscribe(() => {
      const state = this.store.getState();
      this.background = state.router.background;
      this.stack = state.router.stack;
    });

    this.unregister = this.listen();
  }

  public path({ id, type, subType, params }: DetailsRouterPathOptions) {
    const path = subType ? `/details/${type}/${subType}/${id}` : `/details/${type}/${id}`;
    const url = new URL(path, window.location.origin);

    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value) {
          url.searchParams.set(key, value.toString());
        }
      }
    }

    return url.pathname + url.search;
  }

  public isPlayer(location: Location<unknown>) {
    const isGlobalPlaybackPath = location.pathname.includes(routes.playback);
    const isTVPlaybackPath = location.pathname.includes(routes.tv.base + '/');
    return isGlobalPlaybackPath || isTVPlaybackPath;
  }

  public isRecording(asset: Title | Asset) {
    return asset.__createdFromRecording === true;
  }

  /**
   * Resets the router, unregistering the block. This should be called after
   * any other component in the application blocks the navigation.
   */
  public reset() {
    this.unregister();
    this.unregister = this.listen();
  }

  /**
   * Navigates to the background location in history stack.
   */
  public close() {
    if (this.background) {
      this.resetStack();
      this.history.push(this.background);
    } else {
      warn('No background location found.');
    }
  }

  public incrementStack() {
    this.store.dispatch(incrementStack());
  }

  public decrementStack() {
    this.store.dispatch(decrementStack());
  }

  public resetStack() {
    this.store.dispatch(resetStack());
  }

  private push(location: Location<unknown>) {
    // 1. Is not on the details page.
    // 2. Is routing to the details page.
    // 3. Is not in the modal.
    if (this.isDetails(location) && !this.isDetails(this.prevLocation) && !this.background) {
      return this.setBackground();
    }

    // 1. Is on the details page.
    // 2. Is routing to a different page.
    // 3. Is in the modal.
    if (this.isDetails(this.prevLocation) && !this.isDetails(location) && this.background) {
      return this.removeBackground();
    }

    // 1. Is on the details page.
    // 2. Is routing to a different details page.
    // 3. Is in the modal.
    if (this.isDetails(this.prevLocation) && this.isDetails(location) && this.background) {
      this.incrementStack();
    }

    this.setVars();
  }

  private replace(location: Location<unknown>) {
    // Can just be the same as push since we don't
    // actually do any routing, just handling the background.
    this.push(location);
  }

  private pop(location: Location<unknown>) {
    // 1. Is not on the details page.
    // 2. Is routing to the details page.
    // 3. Is not in the modal.
    // 4. Is in the player
    if (!this.isDetails(this.prevLocation) && this.isDetails(location) && !this.background) {
      if (!this.isPlayer(this.prevLocation)) {
        return this.setBackground();
      }
    }

    // 1. Is on the details page.
    // 2. Is routing to a different page.
    // 3. Is in the modal.
    if (this.isDetails(this.prevLocation) && !this.isDetails(location) && this.background) {
      return this.removeBackground();
    }

    // 1. Is on the details page.
    // 2. Is routing to a different details page.
    // 3. Is in the modal.
    if (this.isDetails(this.prevLocation) && this.isDetails(location) && this.background) {
      this.decrementStack();
    }

    // Scroll to the top of the page when navigating back to the details page.
    if (this.isDetails(location)) {
      this.scroll(0, 0);
    }

    this.setVars();
  }

  private listen() {
    return this.history.listen((location, action) => {
      switch (action) {
        case 'PUSH':
          return this.push(location);
        case 'POP':
          return this.pop(location);
        case 'REPLACE':
          return this.replace(location);
        default:
          this.setVars(location);
          break;
      }
    });
  }

  private setVars(location = this.history.location) {
    this.prevLocation = location;
    this.scrollPosition = document.documentElement.scrollTop;
  }

  private setBackground(): false | void {
    this.store.dispatch(setBackgroundLocation(this.prevLocation));
    this.incrementStack();
    this.setVars();
  }

  private removeBackground(): false | void {
    this.store.dispatch(removeBackgroundLocation());
    this.resetStack();

    this.prevLocation = this.history.location;
    this.scroll(0, this.scrollPosition);
  }

  private scroll(x: number, y: number) {
    // Make sure we scroll just before paint, otherwise it will be janky.
    requestAnimationFrame(() => {
      window.scrollTo(x, y);
    });
  }

  private isDetails(location: Location<unknown>) {
    return location.pathname.startsWith('/details');
  }
}
